// istanbul ignore file
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import R from 'ramda';
import CardsGridWrapper from '../../../CardsGridWrapper';
import CardImage from '../../../CardImage';
import CardContent from '../../../CardContent';
import CardElement from '../../../CardElement';

const generateCardClassNames = ({ contentBoxAlignment, hasBackground }) => (
  classNames(
    'card',
    'card--stacked',
    'card--stacked-vertical',
    'cell',
    'align-center',
    'xsmall-16', {
      'align-bottom': contentBoxAlignment.includes('top'),
      'card--no-background': !hasBackground,
    },
  )
);

const generateCardContentClassNames = () => (
  classNames(
    'card__content',
    'card__content--stacked',
    'card__content--stacked-vertical',
  )
);

const generateCardContentBoxClassNames = () => (
  classNames(
    'card__content-box',
    'card__content-box--stacked',
    'xsmall-text--center',
  )
);

const CardStackedVerticalFull = ({
  fullWidth = false,
  fillContainer = false,
  noGutters = false,
  isTitleUnderlined = true,
  hasBackground = true,
  tagHeadingLevel,
  isTagUnderlined = false,
  showStarRating = false,
  headingLevel,
  cardsData,
  isSmallTitle = false,
  cover,
  imgRatingOnSrc,
  imgRatingOffSrc,
  zoomOnHover,
  fixedHeight,
  hideDescription,
  alignCenter,
  openLinkInNewTab = false,
  fullClickableArea = true,
  renderCustomRating,
  ratingIconWidth,
  ratingIconHeight,
  additionalOnClick,
}) => (
  <CardsGridWrapper
    fullWidth={fullWidth}
    fillContainer={fillContainer}
    noGutters={noGutters}
    alignCenter={alignCenter}
  >
    {Array.isArray(cardsData) && (
      cardsData.map(({ contentBoxAlignment, ...card }) => (
        <div
          key={R.prop('id')(card)}
          className={generateCardClassNames({ contentBoxAlignment, hasBackground })}
        >
          <CardElement
            linkUrl={R.path(['link', 'url'])(card)}
            linkText={R.path(['link', 'text'])(card)}
            accessibleLinkText={R.path(['link', 'accessibleText'])(card)}
            openLinkInNewTab={openLinkInNewTab}
            fullClickableArea={fullClickableArea}
          >
            {contentBoxAlignment.includes('bottom') && (
              <CardImage
                src={R.prop('imageSrc')(card)}
                srcset={R.prop('imageSrcSet')(card)}
                alt={R.prop('alt')(card)}
                label={R.prop('label')(card)}
                cover={cover}
                zoomOnHover={zoomOnHover}
                fixedHeight={fixedHeight}
              />
            )}

            {(R.prop('title')(card) || R.prop('description')(card)) && (
              <CardContent
                title={R.prop('title')(card)}
                description={R.prop('description')(card)}
                linkUrl={R.path(['link', 'url'])(card)}
                linkText={R.path(['link', 'text'])(card)}
                tag={R.prop('tag')(card)}
                tagHeadingLevel={tagHeadingLevel}
                isTagUnderlined={isTagUnderlined}
                cardContentClassNames={generateCardContentClassNames()}
                cardContentBoxClassNames={generateCardContentBoxClassNames()}
                headingLevel={headingLevel}
                isTitleUnderlined={isTitleUnderlined}
                showStarRating={showStarRating}
                isSmallTitle={isSmallTitle}
                averageRating={R.prop('averageRating')(card)}
                numberOfReviews={R.prop('numberOfReviews')(card)}
                imgRatingOnSrc={imgRatingOnSrc}
                imgRatingOffSrc={imgRatingOffSrc}
                hideDescription={hideDescription}
                openLinkInNewTab={openLinkInNewTab}
                fullClickableArea={fullClickableArea}
                renderCustomRating={renderCustomRating}
                ratingIconWidth={ratingIconWidth}
                ratingIconHeight={ratingIconHeight}
                bvid={R.prop('bvid', card)}
                slug={R.prop('slug', card)}
                additionalOnClick={additionalOnClick}
                additionalButtonText={R.prop('additionalButtonText', card)}
              />
            )}

            {contentBoxAlignment.includes('top') && (
              <CardImage
                src={R.prop('imageSrc')(card)}
                alt={R.prop('alt')(card)}
                label={R.prop('label')(card)}
                cover={cover}
                zoomOnHover={zoomOnHover}
                fixedHeight={fixedHeight}
              />
            )}
          </CardElement>
        </div>
      ))
    )}
  </CardsGridWrapper>
);

CardStackedVerticalFull.propTypes = {
  fullWidth: PropTypes.bool,
  fillContainer: PropTypes.bool,
  noGutters: PropTypes.bool,
  headingLevel: PropTypes.number,
  isTitleUnderlined: PropTypes.bool,
  tagHeadingLevel: PropTypes.number,
  isTagUnderlined: PropTypes.bool,
  showStarRating: PropTypes.bool,
  cardsData: PropTypes.arrayOf(PropTypes.shape({
    tag: PropTypes.string,
    contentBoxAlignment: PropTypes.oneOf(['bottom', 'top']),
    description: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
    }),
  })),
  hasBackground: PropTypes.bool,
  isSmallTitle: PropTypes.bool,
  cover: PropTypes.bool,
  imgRatingOnSrc: PropTypes.string,
  imgRatingOffSrc: PropTypes.string,
  zoomOnHover: PropTypes.bool,
  fixedHeight: PropTypes.bool,
  hideDescription: PropTypes.bool,
  alignCenter: PropTypes.bool,
  openLinkInNewTab: PropTypes.bool,
  fullClickableArea: PropTypes.bool,
  renderCustomRating: PropTypes.func,
  ratingIconWidth: PropTypes.number,
  ratingIconHeight: PropTypes.number,
};

export default CardStackedVerticalFull;
